import { formatInterval, type AgentStatus } from '../../api/userAgents';

/** Small status badge for a User Agent. */
export function AgentStatusBadge({ status }: { status: AgentStatus }) {
  const styles: Record<AgentStatus, string> = {
    enabled: 'bg-green-500/10 text-green-700 dark:text-green-400',
    paused: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
    archived: 'bg-slate-500/10 text-slate-600 dark:text-slate-400',
  };
  const labels: Record<AgentStatus, string> = {
    enabled: 'Enabled',
    paused: 'Paused',
    archived: 'Archived',
  };
  const dot: Record<AgentStatus, string> = {
    enabled: 'bg-green-500',
    paused: 'bg-amber-500',
    archived: 'bg-slate-400',
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${styles[status] ?? styles.archived}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dot[status] ?? dot.archived}`} aria-hidden="true" />
      {labels[status] ?? status}
    </span>
  );
}

/**
 * Heartbeat interval picker — a slider + number input clamped to the template's
 * [min, max]. The recommended default is pre-filled by the caller.
 */
export function HeartbeatControl({
  value,
  min,
  max,
  onChange,
  disabled = false,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (seconds: number) => void;
  disabled?: boolean;
}) {
  const span = Math.max(1, max - min);
  // Step must divide from `min` so the template default lands on a reachable
  // tick (otherwise the native range input snaps it, e.g. 300 → 360).
  const step = span <= 3600 ? 30 : span <= 86400 ? 60 : 3600;
  const clamp = (v: number) => Math.min(max, Math.max(min, Math.round(v)));
  const snap = (v: number) => {
    const c = clamp(v);
    return Math.min(max, Math.max(min, min + Math.round((c - min) / step) * step));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={snap(value)}
          disabled={disabled}
          onChange={e => onChange(snap(Number(e.target.value)))}
          className="h-1.5 flex-1 cursor-pointer appearance-none rounded-full bg-slate-200 accent-brand-600 dark:bg-slate-700"
          aria-label="Heartbeat interval"
        />
        <div className="flex items-center gap-1">
          <input
            type="number"
            min={min}
            max={max}
            step={step}
            value={clamp(value)}
            disabled={disabled}
            onChange={e => onChange(clamp(Number(e.target.value)))}
            className="w-24 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-950 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          />
          <span className="text-xs text-slate-500">sec</span>
        </div>
      </div>
      <p className="text-xs text-slate-500">
        Every <span className="font-medium text-slate-700 dark:text-slate-300">{formatInterval(clamp(value))}</span>
        {' · '}allowed {formatInterval(min)}–{formatInterval(max)}
      </p>
    </div>
  );
}
